import { useEffect, useRef, useState } from "react";

/**
 * Dropdown whose options have a second line and tags (start week, week
 * names). parts/inputs/select only does single-line options.
 *
 * options: [{ value, label, sub?, tags?: [{ tone, text }], disabled? }]
 * The closed control shows `valueLabel` and `valueSub`.
 */
const RichSelect = ({
  options,
  value,
  onChange,
  valueLabel,
  valueSub,
  ariaLabel,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event) => {
      if (!ref.current?.contains(event.target)) setOpen(false);
    };
    // Capture on window, so Escape closes the list before it closes the modal.
    const onKey = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    ref.current
      ?.querySelector('[aria-selected="true"]:not([disabled])')
      ?.scrollIntoView({ block: "nearest" });
  }, [open]);

  const move = (event) => {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    event.preventDefault();
    const items = [
      ...ref.current.querySelectorAll(".imp-option:not([disabled])"),
    ];
    const at = items.indexOf(document.activeElement);
    const next = event.key === "ArrowDown" ? at + 1 : at - 1;
    items[(next + items.length) % items.length]?.focus();
  };

  return (
    <div ref={ref} className={`imp-dropdown${open ? " open" : ""}`}>
      <button
        type="button"
        className="imp-select"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown") {
            event.preventDefault();
            setOpen(true);
          }
        }}
      >
        <span className="imp-select-value">
          <span>{valueLabel}</span>
          {valueSub && <small>{valueSub}</small>}
        </span>
        <img src="/img/new-icons/arrow-down.svg" alt="" />
      </button>
      {open && (
        // biome-ignore lint/a11y/useSemanticElements: custom listbox with rich options
        <div className="imp-menu" role="listbox" onKeyDown={move}>
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={option.value === value}
              className="imp-option"
              disabled={option.disabled}
              onClick={() => {
                onChange(option.value);
                setOpen(false);
              }}
            >
              <span
                className={`imp-radio${option.value === value ? " on" : ""}`}
              />
              <span className="imp-option-text">
                <span>{option.label}</span>
                {option.sub && <small>{option.sub}</small>}
              </span>
              {option.tags?.length > 0 && (
                <span className="imp-tags">
                  {option.tags.map((tag) => (
                    <span key={tag.text} className={`imp-pill ${tag.tone}`}>
                      {tag.text}
                    </span>
                  ))}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default RichSelect;
